import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import {
  motion,
  useScroll,
  useMotionValueEvent,
  useMotionValue,
  useTransform,
  animate,
  AnimatePresence,
} from 'framer-motion'
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { CircleUserRound, Menu, X } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import BookCallButton from '@/components/BookCallButton'

export type NavbarItem = { label: string; to: string }

export type NavbarAction = {
  label: string
  to: string
  /** Open in a new tab (http links, booking URLs). */
  external?: boolean
  /** Filled/outline pill instead of text link. */
  variant?: 'link' | 'pill'
  /** Open in-app booking modal instead of navigating. */
  booking?: boolean
}

const DEFAULT_NAV_ITEMS: NavbarItem[] = [
  { label: 'About', to: '/#about' },
  { label: 'Services', to: '/#services' },
  { label: 'Approach', to: '/#approach' },
  { label: 'Work', to: '/#work' },
]

const PANEL_EASE = [0.22, 1, 0.36, 1] as const
const SHEET_SPRING_OPEN = { type: 'spring' as const, stiffness: 280, damping: 32, mass: 0.85 }
const SHEET_SPRING_CLOSE = { type: 'spring' as const, stiffness: 380, damping: 40, mass: 0.8 }
/** Hero → floating pill: snappy overshoot. Expand-back is calmer. */
const PILL_SPRING_COLLAPSE = { type: 'spring' as const, bounce: 0.44, duration: 0.78 }
const PILL_SPRING_EXPAND = { type: 'spring' as const, bounce: 0.16, duration: 0.55 }
const LOGO_SPRING = { type: 'spring' as const, bounce: 0.32, duration: 0.7 }

function MobileMenuIcon({ open }: { open: boolean }) {
  return (
    <span className="relative flex h-6 w-6 items-center justify-center">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={open ? 'close' : 'menu'}
          initial={{ opacity: 0, rotate: -70, scale: 0.65 }}
          animate={{ opacity: 1, rotate: 0, scale: 1 }}
          exit={{ opacity: 0, rotate: 70, scale: 0.65 }}
          transition={{ duration: 0.28, ease: PANEL_EASE }}
          className="absolute inset-0 flex items-center justify-center"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}

function isExternalHref(to: string, external?: boolean) {
  return Boolean(external) || /^https?:\/\//i.test(to) || to.startsWith('mailto:')
}

export default function Navbar({
  light = false,
  items,
  actions,
}: {
  light?: boolean
  /** Override default home nav links (e.g. reduced set on landing pages). */
  items?: readonly NavbarItem[]
  /** When set, replaces nav links + Account with these CTAs only. */
  actions?: readonly NavbarAction[]
}) {
  const location = useLocation()
  const { user, loading } = useAuth()
  const ctaOnly = Boolean(actions?.length)
  const navItems = ctaOnly ? [] : (items ?? DEFAULT_NAV_ITEMS)
  const navActions = actions ?? []
  const firstName = user?.user_metadata?.first_name
    || (typeof user?.user_metadata?.full_name === 'string' ? user.user_metadata.full_name.trim().split(/\s+/)[0] : null)
    || (user?.email ? user.email.split('@')[0] : null)
    || 'Account'
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const firstLinkRef = useRef<HTMLAnchorElement>(null)
  const { scrollY } = useScroll()

  const sheetProgress = useMotionValue(0)
  const scrimOpacity = useTransform(sheetProgress, [0, 0.35], [0, 1])
  const sheetClip = useTransform(sheetProgress, [0, 1], ['inset(0 0 100% 0)', 'inset(0 0 0% 0)'])
  const sheetFade = useTransform(sheetProgress, [0, 0.2], [0, 1])

  const pillScale = useMotionValue(1)
  const pillY = useMotionValue(0)
  const prevPillCollapsed = useRef<boolean | null>(null)

  const isNewProjectPage = location.pathname === '/new-project'
  const isLoginPage = location.pathname === '/login'
  const isPrivacyPage = location.pathname === '/privacy'
  const isTermsPage = location.pathname === '/terms'
  const isCollapsed = isScrolled || isMobile || isNewProjectPage || isLoginPage || isPrivacyPage || isTermsPage
  const pillCollapsed = isCollapsed || isMobileMenuOpen
  const useDarkText = light && !isCollapsed

  const showMobileSheet = !ctaOnly && isMobile
  const sheetLinks =
    !isNewProjectPage && !isLoginPage ? navItems : ([] as readonly NavbarItem[])

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setIsScrolled(latest > 50)
  })

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReducedMotion(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) setIsMobileMenuOpen(false)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Drive sheet open/close progress (pill height stays fixed)
  useEffect(() => {
    if (!showMobileSheet) {
      sheetProgress.set(0)
      return
    }
    if (reducedMotion) {
      sheetProgress.set(isMobileMenuOpen ? 1 : 0)
      return
    }
    const controls = animate(
      sheetProgress,
      isMobileMenuOpen ? 1 : 0,
      isMobileMenuOpen ? SHEET_SPRING_OPEN : SHEET_SPRING_CLOSE,
    )
    return () => controls.stop()
  }, [isMobileMenuOpen, showMobileSheet, reducedMotion, sheetProgress])

  // One-shot landing bounce when the hero bar condenses into the floating pill
  useEffect(() => {
    if (prevPillCollapsed.current === null) {
      prevPillCollapsed.current = pillCollapsed
      return
    }
    if (prevPillCollapsed.current === pillCollapsed) return
    prevPillCollapsed.current = pillCollapsed

    if (reducedMotion) {
      pillScale.set(1)
      pillY.set(0)
      return
    }

    if (pillCollapsed) {
      const scaleAnim = animate(pillScale, [1, 1.032, 0.986, 1], {
        duration: 0.62,
        times: [0, 0.34, 0.66, 1],
        ease: [0.22, 1.2, 0.36, 1],
      })
      const yAnim = animate(pillY, [0, 7, -2.5, 0], {
        duration: 0.62,
        times: [0, 0.34, 0.66, 1],
        ease: [0.22, 1.2, 0.36, 1],
      })
      return () => {
        scaleAnim.stop()
        yAnim.stop()
      }
    }

    const scaleAnim = animate(pillScale, 1, PILL_SPRING_EXPAND)
    const yAnim = animate(pillY, 0, PILL_SPRING_EXPAND)
    return () => {
      scaleAnim.stop()
      yAnim.stop()
    }
  }, [pillCollapsed, reducedMotion, pillScale, pillY])

  useEffect(() => {
    if (!isMobileMenuOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isMobileMenuOpen])

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
      const id = window.setTimeout(() => firstLinkRef.current?.focus(), reducedMotion ? 0 : 220)
      return () => {
        window.clearTimeout(id)
        document.body.style.overflow = ''
      }
    }
    document.body.style.overflow = ''
    return () => { document.body.style.overflow = '' }
  }, [isMobileMenuOpen, reducedMotion])

  // Return focus to menu button when closing
  const wasOpenRef = useRef(false)
  useLayoutEffect(() => {
    if (wasOpenRef.current && !isMobileMenuOpen) {
      menuButtonRef.current?.focus()
    }
    wasOpenRef.current = isMobileMenuOpen
  }, [isMobileMenuOpen])

  const closeMenu = () => setIsMobileMenuOpen(false)

  const logoFilterClass = isCollapsed || isMobileMenuOpen || useDarkText
    ? 'img-accent group-hover/logo:brightness-0 group-hover/logo:invert-0 group-hover/logo:saturate-100'
    : 'brightness-0 invert group-hover/logo:invert-0'

  // Use arbitrary HSL alpha — theme colors like border-accent/10 don't apply opacity
  // (tailwind config wraps vars as hsl(...) without <alpha-value>).
  const mobileControlClass = isCollapsed || isMobileMenuOpen || useDarkText
    ? 'border border-black/10 bg-black/[0.01] text-accent hover:bg-black/[0.03] hover:text-primary hover:border-black/10 focus:bg-black/[0.03] focus:text-primary focus:border-black/10'
    : 'border border-black/10 bg-black/[0.01] text-white hover:bg-black/[0.03] hover:text-primary hover:border-black/10 focus:bg-black/[0.03] focus:text-primary focus:border-black/10'

  const accountPillClass = isCollapsed || useDarkText
    ? 'border-accent bg-primary/10 text-accent hover:bg-primary hover:border-primary hover:text-white'
    : 'border-white/20 bg-white/10 text-white hover:bg-white/20 hover:border-white/40'

  const ctaPillClass = isCollapsed || useDarkText
    ? 'border-accent bg-accent text-white hover:bg-primary hover:border-primary'
    : 'border-white/30 bg-white text-accent hover:bg-white/90 hover:border-white'

  return (
    <>
      {/* Scrim — behind sheet, above page */}
      {showMobileSheet && (
        <motion.button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 md:hidden border-0 cursor-default"
          style={{
            opacity: scrimOpacity,
            pointerEvents: isMobileMenuOpen ? 'auto' : 'none',
            backgroundColor: 'hsl(224 71% 4% / 0.48)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
          }}
          onClick={closeMenu}
          tabIndex={isMobileMenuOpen ? 0 : -1}
        />
      )}

      {/* Full-bleed sheet under chrome */}
      {showMobileSheet && (
        <motion.div
          className="fixed inset-0 z-[45] md:hidden bg-white"
          style={{
            clipPath: sheetClip,
            opacity: sheetFade,
            pointerEvents: isMobileMenuOpen ? 'auto' : 'none',
          }}
          aria-hidden={!isMobileMenuOpen}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-[radial-gradient(90%_80%_at_50%_0%,hsl(211_100%_50%/0.06),transparent_70%)]"
          />

          <nav
            className="relative flex h-full flex-col px-8 pb-[max(2rem,env(safe-area-inset-bottom))] pt-28"
            aria-label="Mobile"
          >
            <ul className="flex flex-1 flex-col justify-center gap-1">
              {sheetLinks.map((item, i) => {
                const index = String(i + 1).padStart(2, '0')
                return (
                  <motion.li
                    key={item.label}
                    initial={false}
                    animate={
                      isMobileMenuOpen
                        ? { opacity: 1, y: 0 }
                        : { opacity: 0, y: reducedMotion ? 0 : 18 }
                    }
                    transition={
                      reducedMotion
                        ? { duration: 0 }
                        : {
                            duration: 0.4,
                            delay: isMobileMenuOpen ? 0.08 + i * 0.05 : 0,
                            ease: PANEL_EASE,
                          }
                    }
                  >
                    <Link
                      ref={i === 0 ? firstLinkRef : undefined}
                      to={item.to}
                      onClick={closeMenu}
                      tabIndex={isMobileMenuOpen ? 0 : -1}
                      className={cn(
                        'group flex items-baseline gap-5 py-3.5 border-b border-slate-200/80',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2',
                      )}
                    >
                      <span className="font-mono text-xs text-accent/70 tracking-widest shrink-0 w-7">
                        {index}
                      </span>
                      <span className="relative text-[2rem] leading-none font-bold font-heading text-primary uppercase tracking-tight transition-colors duration-300 group-hover:text-accent group-focus:text-accent">
                        {item.label}
                        <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-accent transition-all duration-300 group-hover:w-full group-focus:w-full" />
                      </span>
                    </Link>
                  </motion.li>
                )
              })}
            </ul>

            <motion.div
              className="mt-auto pt-8 flex flex-col gap-3"
              initial={false}
              animate={
                isMobileMenuOpen
                  ? { opacity: 1, y: 0 }
                  : { opacity: 0, y: reducedMotion ? 0 : 12 }
              }
              transition={
                reducedMotion
                  ? { duration: 0 }
                  : {
                      duration: 0.4,
                      delay: isMobileMenuOpen ? 0.08 + sheetLinks.length * 0.05 : 0,
                      ease: PANEL_EASE,
                    }
              }
            >
              <Link
                to={user ? '/portal' : '/login'}
                onClick={closeMenu}
                tabIndex={isMobileMenuOpen ? 0 : -1}
                className="flex w-full items-center justify-center gap-2.5 rounded-full bg-accent px-8 py-5 text-base font-bold font-heading uppercase tracking-wider text-white shadow-[0_12px_32px_-12px_hsl(211_100%_50%/0.55)] transition-colors hover:bg-primary focus:bg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2"
              >
                <CircleUserRound className="h-5 w-5 shrink-0" />
                {user ? firstName : 'Account'}
              </Link>
              <Link
                to="/new-project"
                onClick={closeMenu}
                tabIndex={isMobileMenuOpen ? 0 : -1}
                className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-primary/15 bg-transparent px-8 py-4 text-base font-bold font-heading uppercase tracking-wider text-primary transition-colors hover:border-primary hover:bg-primary hover:text-white focus:border-primary focus:bg-primary focus:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2"
              >
                New project
              </Link>
            </motion.div>
          </nav>
        </motion.div>
      )}

      <motion.header
        className="fixed top-0 left-0 right-0 z-50 flex justify-center py-4 px-4"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={reducedMotion ? { duration: 0 } : { type: 'spring', bounce: 0.28, duration: 0.7 }}
      >
        <motion.div
          className="relative w-full"
          initial={false}
          animate={{
            maxWidth: pillCollapsed ? '64rem' : '80rem',
            backgroundColor: pillCollapsed ? 'rgba(255, 255, 255, 1)' : 'rgba(255, 255, 255, 0)',
            boxShadow:
              isMobile && isMobileMenuOpen
                ? '0 8px 28px -10px hsl(224 71% 4% / 0.18)'
                : pillCollapsed
                  ? '0 8px 28px -8px hsl(224 71% 4% / 0.16)'
                  : '0 8px 28px -8px hsl(224 71% 4% / 0)',
          }}
          transition={
            reducedMotion
              ? { duration: 0 }
              : {
                  maxWidth: pillCollapsed ? PILL_SPRING_COLLAPSE : PILL_SPRING_EXPAND,
                  backgroundColor: pillCollapsed
                    ? { duration: 0.38, ease: PANEL_EASE }
                    : { duration: 0.22, ease: [0.4, 0, 1, 1] },
                  boxShadow: pillCollapsed
                    ? PILL_SPRING_COLLAPSE
                    : { duration: 0.14, ease: [0.4, 0, 1, 1] },
                }
          }
          style={{
            borderRadius: isMobile ? 32 : 9999,
            scale: pillScale,
            y: pillY,
            transformOrigin: '50% 50%',
          }}
        >
          {/* Top bar — stable height; menu opens as a separate sheet */}
          <div className="relative z-10 flex w-full items-center justify-between gap-4 px-4 py-3 md:gap-8 md:px-6">
            <motion.div
              className="flex items-center"
              initial={false}
              animate={{ height: isCollapsed ? 44 : 56 }}
              transition={reducedMotion ? { duration: 0 } : LOGO_SPRING}
            >
              <Link
                to="/"
                className="flex items-center gap-3 h-full group/logo"
                onClick={(e) => {
                  if (isMobileMenuOpen) closeMenu()
                  if (location.pathname === '/') {
                    e.preventDefault()
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }
                }}
              >
                <img
                  src="/assets/gigafish-brandmark.svg"
                  alt="Gigafish"
                  className={`h-full w-auto group-hover/logo:scale-110 transition-all duration-300 ${logoFilterClass}`}
                />
                <img
                  src="/assets/gigafish-wordmark.svg"
                  alt="Gigafish"
                  className={`w-auto mb-1 transition-all duration-300 block md:hidden xl:block ${isCollapsed ? 'h-6' : 'h-7'} ${logoFilterClass}`}
                />
              </Link>
            </motion.div>

            <div className="flex items-center justify-end">
              <nav className={`hidden md:flex items-center gap-6 lg:gap-8 text-sm font-bold font-heading uppercase transition-colors duration-300 ${isCollapsed ? 'text-accent' : useDarkText ? 'text-accent' : 'text-white/80'}`}>
                {ctaOnly ? (
                  navActions.map((action) => {
                    const isPill = action.variant === 'pill'
                    const className = isPill
                      ? `inline-flex items-center gap-2 px-4 py-2 rounded-full font-heading uppercase text-sm font-bold border transition-all duration-300 ${ctaPillClass}`
                      : 'transition-colors relative group hover:text-primary focus:text-primary'
                    if (action.booking) {
                      return (
                        <BookCallButton key={action.label} className={className}>
                          {action.label}
                        </BookCallButton>
                      )
                    }
                    const external = isExternalHref(action.to, action.external)
                    if (external) {
                      return (
                        <a
                          key={action.label}
                          href={action.to}
                          target={action.to.startsWith('mailto:') ? undefined : '_blank'}
                          rel={action.to.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                          className={className}
                        >
                          {action.label}
                        </a>
                      )
                    }
                    return (
                      <Link key={action.label} to={action.to} className={className}>
                        {action.label}
                        {!isPill && (
                          <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                        )}
                      </Link>
                    )
                  })
                ) : (
                  <>
                    {!isNewProjectPage && !isLoginPage && (
                      <>
                        {navItems.map((item) => (
                          <Link
                            key={item.label}
                            to={item.to}
                            className="transition-colors relative group hover:text-primary focus:text-primary"
                          >
                            {item.label}
                            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
                          </Link>
                        ))}
                      </>
                    )}
                    {!loading && (
                      <Link
                        to={user ? '/portal' : '/login'}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-heading uppercase text-sm font-bold border transition-all duration-300 ${accountPillClass}`}
                      >
                        <CircleUserRound className="h-[1.4rem] w-[1.4rem] shrink-0 -ml-[0.25rem]" />
                        {user ? firstName : 'Account'}
                      </Link>
                    )}
                  </>
                )}
              </nav>

              <div className="md:hidden flex items-center">
                {ctaOnly ? (
                  (() => {
                    const primary =
                      navActions.find((action) => action.variant === 'pill') ?? navActions[navActions.length - 1]
                    if (!primary) return null
                    const className = `inline-flex items-center gap-2 px-4 py-2 rounded-full font-heading uppercase text-sm font-bold border transition-all duration-300 ${ctaPillClass}`
                    if (primary.booking) {
                      return (
                        <BookCallButton className={className}>
                          {primary.label}
                        </BookCallButton>
                      )
                    }
                    const external = isExternalHref(primary.to, primary.external)
                    if (external) {
                      return (
                        <a
                          href={primary.to}
                          target={primary.to.startsWith('mailto:') ? undefined : '_blank'}
                          rel={primary.to.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                          className={className}
                        >
                          {primary.label}
                        </a>
                      )
                    }
                    return (
                      <Link to={primary.to} className={className}>
                        {primary.label}
                      </Link>
                    )
                  })()
                ) : !loading && user && isNewProjectPage ? (
                  <Link
                    to="/portal"
                    className="inline-flex items-center justify-center p-2 rounded-full font-heading text-sm font-bold border border-accent bg-primary/10 text-accent hover:bg-primary hover:border-primary hover:text-white transition-colors"
                    aria-label="Go to client portal"
                  >
                    <CircleUserRound className="h-[1.4rem] w-[1.4rem] shrink-0" />
                  </Link>
                ) : !loading && user ? (
                  <Button
                    ref={menuButtonRef}
                    size="icon"
                    variant="ghost"
                    className={`rounded-full transition-colors duration-300 ${mobileControlClass}`}
                    onClick={() => setIsMobileMenuOpen((o) => !o)}
                    aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={isMobileMenuOpen}
                  >
                    <MobileMenuIcon open={isMobileMenuOpen} />
                  </Button>
                ) : isNewProjectPage || isLoginPage ? (
                  <Link
                    to="/login"
                    className={`inline-flex items-center gap-2 font-heading uppercase text-sm font-bold px-4 py-2 rounded-full border transition-colors ${accountPillClass}`}
                    aria-label="Account"
                  >
                    <CircleUserRound className="h-[1.4rem] w-[1.4rem] shrink-0" />
                    Account
                  </Link>
                ) : (
                  <Button
                    ref={menuButtonRef}
                    size="icon"
                    variant="ghost"
                    className={`rounded-full transition-colors duration-300 ${mobileControlClass}`}
                    onClick={() => setIsMobileMenuOpen((o) => !o)}
                    aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={isMobileMenuOpen}
                  >
                    <MobileMenuIcon open={isMobileMenuOpen} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.header>
    </>
  )
}
